class ContactFormComponent extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('#contact-form');
    this.allPages = this.querySelectorAll('.contact-form__page');
    this.successBlock = this.querySelector('#ContactFormSuccess');
    this.errorBlock = this.querySelector('#ContactFormError');
    this.countrySelect = this.querySelector('#AddressCountry');
    this.provinceSelect = this.querySelector('#AddressProvince');
    this.provinceContainer = this.querySelector('#AddressProvinceContainer');
    this.submitButton = this.querySelector('button[type="submit"]');

    this.selectedIndex = null;
    this.selectedIncidentType = 'OO';
    this.initialQuestionLabel = '';
    this.secondaryQuestionLabel = '';

    this.init();
  }

  init() {
    this.addEventListener('click', this.handleClick.bind(this));

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));

      this.form.querySelectorAll('input, textarea, select').forEach(input => {
        input.addEventListener('input', () => this.clearError(input));
        input.addEventListener('change', () => this.clearError(input));
      });
    }

    if (this.countrySelect) {
      this.countrySelect.addEventListener('change', this.updateProvinces.bind(this));
    }

    this.updateProvinces();
  }

  toggleUI(state) {
    if (!this.form || !this.successBlock || !this.errorBlock) return;

    const pageTop = this.querySelector('.is-active .contact-form__page-top');
    if (pageTop) pageTop.style.display = (state === 'form') ? 'block' : 'none';

    this.form.style.display = (state === 'form') ? 'block' : 'none';
    this.successBlock.style.display = (state === 'success') ? 'flex' : 'none';
    this.errorBlock.style.display = (state === 'error') ? 'flex' : 'none';
  }

  goToPage(pageId) {
    const targetPage = this.querySelector(`[data-page-id="${pageId}"]`);
    if (targetPage) {
      this.allPages.forEach(page => page.classList.remove('is-active'));
      targetPage.classList.add('is-active');

      const focusTarget = targetPage.querySelector('.js-form-toggle, h2, h3, p');
      if (focusTarget) {
        if (!['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'A'].includes(focusTarget.tagName)) {
          focusTarget.setAttribute('tabindex', '-1');
        }
        focusTarget.focus({ preventScroll: true });
      }
    }
  }

  updateConditionalFields() {
    const fields = this.querySelectorAll('[data-show]');
    fields.forEach(field => {
      const allowed = field.dataset.show.split(',').map(i => i.trim());
      const isVisible = allowed.includes(this.selectedIndex);

      field.style.display = isVisible ? '' : 'none';

      field.querySelectorAll('input, textarea, select').forEach(input => {
        input.required = isVisible;
        input.ariaHidden = !isVisible;
        if (!isVisible) {
          input.value = '';
          this.clearError(input);
        }
      });
    });
  }

  validate() {
    let isValid = true;
    const requiredFields = this.form.querySelectorAll('[required]');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    requiredFields.forEach(field => {
      let fieldValid = true;
      const value = field.value.trim();

      if (!value) {
        fieldValid = false;
      } else if (field.type === 'email' && !emailRegex.test(value)) {
        fieldValid = false;
      }

      if (!fieldValid) {
        isValid = false;
        this.showError(field);
      } else {
        this.clearError(field);
      }
    });

    return isValid;
  }

  showError(field) {
    const row = field.closest('.form-row');
    if (row) {
      row.classList.add('is-error');
      const errorMsg = row.querySelector('.form-field__error');
      if (errorMsg) errorMsg.style.display = 'block';
    }
    field.setAttribute('aria-invalid', 'true');
  }

  clearError(field) {
    const row = field.closest('.form-row');
    if (row) {
      row.classList.remove('is-error');
      const errorMsg = row.querySelector('.form-field__error');
      if (errorMsg) errorMsg.style.display = 'none';
    }
    field.removeAttribute('aria-invalid');
  }

  escapeXml(unsafe) {
    if (!unsafe) return '';
    return unsafe.replace(/[<>&"']/g, (c) => {
      const map = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' };
      return map[c] || c;
    });
  }

  constructXML() {
    const formData = new FormData(this.form);
    const rawName = formData.get('contact[full_name]') || '';
    const nameParts = rawName.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

    const uuid = crypto.randomUUID();
    const timestamp = new Date().toISOString();
    const locale = Theme.otterVars.locale || 'en_US';

    const rawMsg = formData.get('contact[body]') || formData.get('contact[damage_details]') || '';
    const cleanMessage = this.escapeXml(rawMsg);

    let desc = `Initial Question: ${this.escapeXml(this.initialQuestionLabel)}`;
    if (this.secondaryQuestionLabel) {
      desc += `, General Question: ${this.escapeXml(this.secondaryQuestionLabel)}`;
    }
    desc += `, Message: ${cleanMessage}`;

    return `<?xml version="1.0" encoding="UTF-8"?>
      <CustomerIncidentCreateRequest xmlns="http://schemas.otterbox.com/DemandwareMessages-1.0.0/">
        <MessageHeader>
          <ID>${uuid}</ID>
          <CorrelationId>${uuid}</CorrelationId>
          <OriginatingApplicationName>Shopify</OriginatingApplicationName>
          <OriginatingApplicationInstanceCode>PRD</OriginatingApplicationInstanceCode>
          <OriginatingHostname>${window.location.hostname}</OriginatingHostname>
          <CreateTimestamp>${timestamp}</CreateTimestamp>
        </MessageHeader>
        <CustomerIncident>
          <EcommerceSiteID>${Theme.otterVars.mulesoft_site_id}</EcommerceSiteID>
          <CustomerInteractionChannelName>Web</CustomerInteractionChannelName>
          <OriginalOrderNumber>${this.escapeXml(formData.get('contact[order_number]') || '')}</OriginalOrderNumber>
          <CustomerProvidedIncidentInformation>
            <IncidentTypeName>${this.selectedIncidentType}</IncidentTypeName>
            <Description>${desc}</Description>
            <BusinessOrganization>${this.escapeXml(formData.get('contact[company]') || 'undefined')}</BusinessOrganization>
            <OrderNumber>${this.escapeXml(formData.get('contact[order_number]') || '')}</OrderNumber>
            <ProductPurchaseDate>${this.escapeXml(formData.get('contact[purchase_date]') || '')}</ProductPurchaseDate>
            <ProductPurchaseLocation>${this.escapeXml(formData.get('contact[purchase_place]') || '')}</ProductPurchaseLocation>
            <Device>${this.escapeXml(formData.get('contact[device]') || '')}</Device>
            <DeviceSeries>${this.escapeXml(formData.get('contact[serial]') || '')}</DeviceSeries>
            <Color>${this.escapeXml(formData.get('contact[color]') || '')}</Color>
          </CustomerProvidedIncidentInformation>
          <CustomerContact>
            <FirstName>${this.escapeXml(firstName)}</FirstName>
            <LastName>${this.escapeXml(lastName)}</LastName>
            <EmailAddress>${this.escapeXml(formData.get('contact[email]'))}</EmailAddress>
            <Phones><Phone><PhoneNumber>${this.escapeXml(formData.get('contact[phone]'))}</PhoneNumber></Phone></Phones>
            <Address>
              <AddressLine1>${this.escapeXml(formData.get('contact[address_1]'))}</AddressLine1>
              <AddressLine2>${this.escapeXml(formData.get('contact[address_2]') || '')}</AddressLine2>
              <CityName>${this.escapeXml(formData.get('contact[city]'))}</CityName>
              <PostalCode>${this.escapeXml(formData.get('contact[zip]'))}</PostalCode>
              <SubdivisionCode>${this.escapeXml(formData.get('contact[province]') || '')}</SubdivisionCode>
              <CountryCode>${this.escapeXml(formData.get('contact[country]'))}</CountryCode>
            </Address>
            <PreferredLocaleCode>${locale}</PreferredLocaleCode>
          </CustomerContact>
          <CustomerBillingContact>
            <FirstName>${this.escapeXml(firstName)}</FirstName>
            <LastName>${this.escapeXml(lastName)}</LastName>
            <Phones><Phone><PhoneNumber>${this.escapeXml(formData.get('contact[phone]'))}</PhoneNumber></Phone></Phones>
            <Address>
              <AddressLine1>${this.escapeXml(formData.get('contact[address_1]'))}</AddressLine1>
              <AddressLine2>${this.escapeXml(formData.get('contact[address_2]') || '')}</AddressLine2>
              <CityName>${this.escapeXml(formData.get('contact[city]'))}</CityName>
              <PostalCode>${this.escapeXml(formData.get('contact[zip]'))}</PostalCode>
              <SubdivisionCode>${this.escapeXml(formData.get('contact[province]') || '')}</SubdivisionCode>
              <CountryCode>${this.escapeXml(formData.get('contact[country]'))}</CountryCode>
            </Address>
          </CustomerBillingContact>
        </CustomerIncident>
      </CustomerIncidentCreateRequest>`;
  }

  handleClick(e) {
    const btnToggle = e.target.closest('.js-form-toggle');
    const btnShow = e.target.closest('.js-form-show');
    const btnSubmit = e.target.closest('.js-submit-button');

    if (btnToggle) {
      const { target } = btnToggle.dataset;
      const currentId = btnToggle.closest('.contact-form__page')?.dataset.pageId;
      if (target === "2") this.secondaryQuestionLabel = '';
      target ? this.goToPage(target) : (currentId == "1" ? this.goToPage("2") : this.goToPage("1"));
    }

    if (btnShow) {
      const parent = btnShow.closest('.js-list-options');
      const currentId = btnShow.closest('.contact-form__page')?.dataset.pageId;

      parent.querySelectorAll('.form-option-button').forEach(btn => btn.classList.remove('is-selected'));
      btnShow.classList.add('is-selected');

      const submitBtn = parent.querySelector('.js-submit-button');
      if (submitBtn) {
        submitBtn.style.pointerEvents = 'auto';
        submitBtn.style.opacity = '1';
        submitBtn.dataset.nextPage = btnShow.dataset.targetPage || '';
        submitBtn.dataset.nextIndex = btnShow.dataset.index || '';

        const label = btnShow.querySelector('h6')?.innerText || '';
        if (currentId === "2") {
          this.initialQuestionLabel = label;
          this.secondaryQuestionLabel = '';
        } else {
          this.secondaryQuestionLabel = label;
        }

        if (btnShow.dataset.incident) this.selectedIncidentType = btnShow.dataset.incident;
      }
    }

    if (btnSubmit) {
      const { nextPage, nextIndex } = btnSubmit.dataset;
      if (nextPage) {
        this.goToPage(nextPage);
      } else if (nextIndex) {
        this.selectedIndex = nextIndex;
        this.updateConditionalFields();
        this.goToPage('ContactForm');
      }
    }
  }

  async handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (!this.validate()) return;

    this.submitButton.disabled = true;
    const originalBtnText = this.submitButton.innerText;
    this.submitButton.innerText = 'Sending...';

    let auth = {};
    try { auth = JSON.parse(Theme.otterVars.mulesoft_auth_headers); } catch (err) {}

    try {
      const response = await fetch(Theme.otterVars.mulesoft_endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/xml'
        },
        body: this.constructXML()
      });

      if (response.status === 201) {
        this.toggleUI('success');
        this.successBlock.setAttribute('tabindex', '-1');
        this.successBlock.focus({ preventScroll: true });
      } else {
        throw new Error(`HTTP Error: ${response.status}`);
      }
    } catch (err) {
      console.error('Submission failed:', err);
      this.toggleUI('error');
      this.submitButton.disabled = false;
      this.submitButton.innerText = originalBtnText;
      this.errorBlock.setAttribute('tabindex', '-1');
      this.errorBlock.focus({ preventScroll: true });
    }
  }

  updateProvinces() {
    if (!this.countrySelect || !this.provinceSelect) return;

    const countryIsoCode = this.countrySelect.value;
    const allData = window.Countries;

    if (!allData) return;

    const countryData = Object.values(allData).find(c => c.code === countryIsoCode);

    this.provinceSelect.innerHTML = '';

    if (countryData && countryData.province_codes && Object.keys(countryData.province_codes).length > 0) {
      for (const [name, code] of Object.entries(countryData.province_codes)) {
        const option = document.createElement('option');
        option.value = code;
        option.textContent = name;
        this.provinceSelect.appendChild(option);
      }
        this.showProvinceContainer(true);
    } else {
        this.showProvinceContainer(false);
    }
  }

  /**
   * @param {boolean} isVisible - show or hide the province container
   */
  showProvinceContainer(isVisible) {
    if (!this.provinceContainer || !this.provinceSelect) return;

    if (isVisible) {
        this.provinceContainer.style.display = 'block';
        this.provinceSelect.disabled = false;
        this.provinceSelect.setAttribute('required', 'required');
    } else {
        this.provinceContainer.style.display = 'none';
        this.provinceSelect.disabled = true;
        this.provinceSelect.removeAttribute('required');
        this.provinceSelect.value = '';
    }
  }
}

customElements.define('contact-form-component', ContactFormComponent);
